import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, Sparkles, Moon, Heart, Star, Save, 
  Loader2, Calendar, ChevronLeft, ChevronRight, Flame,
  CheckCircle2, PenLine, TrendingUp, Brain, RefreshCw
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import JournalStatsCards from "@/components/journal/JournalStatsCards";
import JournalEntriesList from "@/components/journal/JournalEntriesList";
import { MOOD_OPTIONS } from "@/lib/journal-constants";

const Journal = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todayData, setTodayData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // AI Insights state
  const [aiInsight, setAiInsight] = useState(null);
  const [loadingInsight, setLoadingInsight] = useState(false);
  
  // Form state
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("");
  const [gratitude, setGratitude] = useState("");
  const [intentions, setIntentions] = useState("");
  const [questionResponses, setQuestionResponses] = useState({});

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchTodayData();
    fetchEntries();
    fetchStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTodayData = async () => {
    try {
      const res = await axios.get(`${API}/journal/today`, { headers, withCredentials: true });
      setTodayData(res.data);
      
      // If there's an existing entry, populate the form
      if (res.data.existing_entry) {
        const entry = res.data.existing_entry;
        setContent(entry.content || "");
        setMood(entry.mood || "");
        setGratitude(entry.gratitude || "");
        setIntentions(entry.intentions || "");
        
        // Restore question responses
        if (entry.guided_questions) {
          const responses = {};
          entry.guided_questions.forEach(q => {
            responses[q.question] = q.response;
          });
          setQuestionResponses(responses);
        }
      }
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/journal/entries?limit=30`, { headers, withCredentials: true });
      setEntries(res.data.entries);
    } catch (error) {
      // data loads with fallback state
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/journal/stats`, { headers, withCredentials: true });
      setStats(res.data);
    } catch (error) {
      // data loads with fallback state
    }
  };

  const fetchAiInsight = async () => {
    setLoadingInsight(true);
    try {
      const res = await axios.get(`${API}/journal/ai-insights`, { headers, withCredentials: true });
      setAiInsight(res.data);
    } catch (error) {
      // data loads with fallback state
      toast.error("Failed to get AI insights");
    } finally {
      setLoadingInsight(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error("Please write something in your journal");
      return;
    }

    setSaving(true);
    try {
      // Compile guided question responses
      const guidedQuestions = Object.entries(questionResponses)
        .filter(([_, response]) => response?.trim())
        .map(([question, response]) => ({ question, response }));

      await axios.post(`${API}/journal/entry`, {
        content,
        mood,
        gratitude,
        intentions,
        cycle_phase: todayData?.cycle_phase,
        moon_sign: todayData?.moon_sign,
        guided_questions: guidedQuestions
      }, { headers, withCredentials: true });

      toast.success("Journal entry saved! 📝");
      fetchEntries();
      fetchStats();
      fetchTodayData();
    } catch (error) {
      toast.error("Failed to save journal entry");
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionResponse = (question, response) => {
    setQuestionResponses(prev => ({
      ...prev,
      [question]: response
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="journal-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-mystic-300" />
          Sacred Journal
        </h1>
        <p className="text-mystic-200/70">
          Daily guided reflections aligned with your cycle and the cosmos
        </p>
      </motion.div>

      {/* Stats Cards */}
      <JournalStatsCards stats={stats} />

      {/* Main Content */}
      <Tabs defaultValue="write" className="w-full">
        <TabsList className="grid grid-cols-2 bg-mystic-800/50 p-1 rounded-xl mb-6">
          <TabsTrigger 
            value="write" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg"
          >
            <PenLine className="w-4 h-4 mr-2" />
            Today's Journal
          </TabsTrigger>
          <TabsTrigger 
            value="history" 
            className="data-[state=active]:bg-mystic-700 data-[state=active]:text-white text-mystic-200/70 rounded-lg"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Past Entries
          </TabsTrigger>
        </TabsList>

        {/* Write Tab */}
        <TabsContent value="write" className="space-y-6">
          {/* Today's Cosmic Context */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {/* Cycle Phase Card */}
            <Card className="glass bg-gradient-to-br from-pink-500/10 to-transparent">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-mystic-300 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Cycle Phase: {todayData?.cycle_phase || "Unknown"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {todayData?.cycle_theme}
                </h3>
                <p className="text-mystic-200/80 text-sm italic">
                  "{todayData?.cycle_affirmation}"
                </p>
              </CardContent>
            </Card>

            {/* Moon Sign Card */}
            <Card className="glass bg-gradient-to-br from-indigo-500/10 to-transparent">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-mystic-300 flex items-center gap-2">
                  <Moon className="w-4 h-4" />
                  Moon in {todayData?.moon_sign} {todayData?.moon_emoji}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {todayData?.moon_energy}
                </h3>
                <p className="text-mystic-200/80 text-sm">
                  {todayData?.moon_phase} • {todayData?.moon_degree}°
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Guided Questions */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-moon-gold" />
                  Guided Reflection Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Cycle-based questions */}
                <div>
                  <h4 className="text-mystic-300 font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Based on your {todayData?.cycle_phase} phase:
                  </h4>
                  <div className="space-y-4">
                    {todayData?.cycle_questions?.slice(0, 2).map((question, idx) => (
                      <div key={`cycle-${idx}`} className="space-y-2">
                        <Label className="text-mystic-100">{question}</Label>
                        <Textarea
                          placeholder="Your reflection..."
                          value={questionResponses[question] || ""}
                          onChange={(e) => handleQuestionResponse(question, e.target.value)}
                          className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[80px]"
                          data-testid={`cycle-question-${idx}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Moon-based questions */}
                <div>
                  <h4 className="text-mystic-300 font-medium mb-3 flex items-center gap-2">
                    <Moon className="w-4 h-4" />
                    Moon in {todayData?.moon_sign} invites you to explore:
                  </h4>
                  <div className="space-y-4">
                    {todayData?.moon_questions?.slice(0, 2).map((question, idx) => (
                      <div key={`moon-${idx}`} className="space-y-2">
                        <Label className="text-mystic-100">{question}</Label>
                        <Textarea
                          placeholder="Your reflection..."
                          value={questionResponses[question] || ""}
                          onChange={(e) => handleQuestionResponse(question, e.target.value)}
                          className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[80px]"
                          data-testid={`moon-question-${idx}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Free Writing & Mood */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <PenLine className="w-5 h-5 text-mystic-300" />
                  Free Writing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mood Selection */}
                <div>
                  <Label className="text-mystic-100 mb-3 block">How are you feeling today?</Label>
                  <div className="flex flex-wrap gap-2">
                    {MOOD_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setMood(option.value)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          mood === option.value
                            ? "bg-mystic-600 text-white border border-mystic-300"
                            : "glass-light text-mystic-200/70 hover:text-white hover:bg-mystic-700/50"
                        }`}
                        data-testid={`mood-${option.value}`}
                      >
                        {option.emoji} {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Free Writing */}
                <div>
                  <Label className="text-mystic-100">What's on your heart and mind?</Label>
                  <Textarea
                    placeholder="Write freely... this is your sacred space."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[150px] mt-2"
                    data-testid="journal-content"
                  />
                </div>

                {/* Gratitude */}
                <div>
                  <Label className="text-mystic-100 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-400" />
                    What are you grateful for today?
                  </Label>
                  <Textarea
                    placeholder="Three things I'm grateful for..."
                    value={gratitude}
                    onChange={(e) => setGratitude(e.target.value)}
                    className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[80px] mt-2"
                    data-testid="journal-gratitude"
                  />
                </div>

                {/* Intentions */}
                <div>
                  <Label className="text-mystic-100 flex items-center gap-2">
                    <Star className="w-4 h-4 text-moon-gold" />
                    What intentions do you set for today/tomorrow?
                  </Label>
                  <Textarea
                    placeholder="My intentions are..."
                    value={intentions}
                    onChange={(e) => setIntentions(e.target.value)}
                    className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[80px] mt-2"
                    data-testid="journal-intentions"
                  />
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-mystic-600 hover:bg-mystic-500 text-white rounded-full py-6"
                  data-testid="save-journal-btn"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  {todayData?.has_entry_today ? "Update Today's Entry" : "Save Journal Entry"}
                </Button>

                {todayData?.has_entry_today && (
                  <p className="text-center text-mystic-300 text-sm flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    You've already journaled today! You can update your entry.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* AI Insights Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="glass bg-gradient-to-br from-purple-500/10 to-transparent">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-400" />
                    AI-Powered Insights
                  </span>
                  <Button
                    onClick={fetchAiInsight}
                    disabled={loadingInsight}
                    variant="ghost"
                    size="sm"
                    className="text-mystic-300 hover:text-white"
                    data-testid="get-ai-insight-btn"
                  >
                    {loadingInsight ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {aiInsight ? (
                  aiInsight.message ? (
                    <div className="text-center py-6" data-testid="ai-insight-message">
                      <Brain className="w-12 h-12 text-mystic-500 mx-auto mb-3" />
                      <p className="text-mystic-200/70">{aiInsight.message}</p>
                      {aiInsight.entries_count !== undefined && (
                        <p className="text-mystic-400 text-sm mt-2">
                          Current entries: {aiInsight.entries_count}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="ai-insight-content">
                      {aiInsight.context && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          <span className="text-xs px-2 py-1 glass-light rounded-full text-mystic-300">
                            🌙 {aiInsight.context.moon_phase}
                          </span>
                          <span className="text-xs px-2 py-1 glass-light rounded-full text-mystic-300">
                            ✨ Moon in {aiInsight.context.moon_sign}
                          </span>
                          <span className="text-xs px-2 py-1 glass-light rounded-full text-mystic-300">
                            🌸 {aiInsight.context.cycle_phase} phase
                          </span>
                          <span className="text-xs px-2 py-1 glass-light rounded-full text-mystic-300">
                            📝 {aiInsight.context.entries_analyzed} entries analyzed
                          </span>
                        </div>
                      )}
                      <div className="text-mystic-100 whitespace-pre-wrap leading-relaxed prose prose-invert max-w-none">
                        {aiInsight.insights}
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-center py-6">
                    <Brain className="w-12 h-12 text-mystic-500 mx-auto mb-3" />
                    <p className="text-mystic-200/70 mb-4">
                      Get personalized insights based on your journal entries, cycle phase, and moon energy.
                    </p>
                    <Button
                      onClick={fetchAiInsight}
                      disabled={loadingInsight}
                      className="bg-purple-600 hover:bg-purple-500 text-white rounded-full"
                      data-testid="generate-insight-btn"
                    >
                      {loadingInsight ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate AI Insight
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <JournalEntriesList
            entries={entries}
            selectedEntry={selectedEntry}
            onSelect={setSelectedEntry}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Journal;
